import dayjs from "dayjs";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { Icon } from "../../../components/Icon/Icon";
import { colors } from "../../../styles/colors";
import { CustomChip } from "../../../components/CustomChip/CustomChip";
import { translateSinisterStatus } from "../../Claims/types";
import { TranslateFn } from "../../../utils/types";

const iconButtonSx = {
  padding: "8px",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
};

/**
 * Nombre de documents attaches a une intervention.
 *
 * `strongbox_names` est le GROUP_CONCAT produit par
 * Fleet::getEventVehicleById : l'API le renvoie deja sur chaque ligne, mais
 * rien ne le lisait. On s'en sert pour dire A L'AVANCE si la ligne a quelque
 * chose a montrer, plutot que d'ouvrir une modale pour y trouver un ecran
 * vide.
 *
 * Le GROUP_CONCAT est DISTINCT : deux documents portant exactement le meme
 * nom comptent pour un. C'est un compteur d'affichage, pas une verite
 * comptable — mais la distinction "zero / non-zero" qui pilote l'etat
 * desactive, elle, est fiable.
 */
const countDocuments = (data: Record<string, any>): number => {
  const names = data?.strongbox_names;
  if (typeof names !== "string" || names.trim() === "") return 0;
  return names
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean).length;
};

/**
 * Colonnes de l'historique des interventions.
 *
 * Prend `t` en argument plutot que d'appeler i18n.t() au niveau module : une
 * constante de module est evaluee une seule fois, a l'import, et resterait
 * figee dans la langue active au demarrage. Meme convention que
 * getColumnsListVehicle et containers/Sidebar/DataSideBar.ts.
 */
export const columnsHistory = (
  t: TranslateFn,
  onOpenDocuments: (eventId: number) => void,
  onRowClick?: (eventId: number) => void
) => [
  {
    header: t("common.date", "Date"),
    field: "date",
    render: (data: Record<string, any>) => {
      return (
        <div>
          {data?.datetime ? dayjs(data.datetime).format("DD-MM-YYYY") : ""}
        </div>
      );
    },
  },
  { header: t("event.interventionType", "Service type"), field: "event_type_name" },
  { header: t("event.interventionName", "Service name"), field: "name" },
  {
    header: t("common.documents", "Documents"),
    field: "documents_action",
    render: (data: Record<string, any>) => {
      const count = countDocuments(data);
      const hasDocuments = count > 0;

      // Un bouton toujours identique, qu'il y ait zero ou trois documents,
      // oblige a cliquer pour decouvrir qu'il n'y a rien. Le badge repond
      // avant le clic, et l'etat desactive evite la modale vide.
      const button = (
        <span>
          <IconButton
            aria-label={
              hasDocuments
                ? // `total`, pas `count` : count est le selecteur de pluriel
                  // d'i18next et declencherait une resolution _one / _other.
                  t("event.openDocumentsCount", "Open {{total}} document(s)", {
                    total: count,
                  })
                : t("event.noDocumentsShort", "No documents")
            }
            disabled={!hasDocuments}
            onClick={(e) => {
              e.stopPropagation(); // ne pas declencher le clic de ligne
              onOpenDocuments(data.id);
            }}
            size="small"
            sx={iconButtonSx}
          >
            <Badge
              badgeContent={count}
              color="primary"
              invisible={!hasDocuments}
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  height: "16px",
                  minWidth: "16px",
                },
              }}
            >
              <Icon
                name="file"
                width={20}
                height={20}
                color={hasDocuments ? colors.primary : colors.textSecondary}
              />
            </Badge>
          </IconButton>
        </span>
      );

      // Le <span> ci-dessus n'est pas decoratif : un <button> desactive
      // n'emet aucun evenement souris, donc un Tooltip greffe dessus ne
      // s'afficherait jamais — precisement quand l'explication est utile.
      return (
        <Tooltip
          title={
            hasDocuments
              ? t("event.openDocuments", "View attached documents")
              : t("event.noDocumentsShort", "No documents")
          }
        >
          {button}
        </Tooltip>
      );
    },
  },
  {
    header: t("common.actions", "Actions"),
    field: "event_action",
    render: (data: Record<string, any>) => {
      return (
        <Tooltip title={t("event.viewDetail", "View service entry")}>
          <IconButton
            aria-label={t("event.viewDetail", "View service entry")}
            onClick={(e) => {
              e.stopPropagation(); // ne pas declencher le clic de ligne
              if (onRowClick) {
                onRowClick(data.id);
              }
            }}
            size="small"
            sx={iconButtonSx}
          >
            <Icon name="eye" width={20} height={20} color={colors.primary} />
          </IconButton>
        </Tooltip>
      );
    },
  },
];

export const columnsClaims = (
  t: TranslateFn,
  onViewClaim: (sinisterId: number) => void
) => [
  {
    header: t("common.date", "Date"),
    field: "sinister_at",
    render: (data: Record<string, any>) => {
      return (
        <div>
          {data?.sinister_at ? dayjs(data.sinister_at).format("DD-MM-YYYY") : ""}
        </div>
      );
    },
  },
  { header: t("common.name", "Name"), field: "name" },
  {
    header: t("vehicule.status", "Status"),
    field: "sinister_status",
    render: (data: Record<string, any>) => {
      return (
        <CustomChip
          // Traduction du CODE, comme pour le statut vehicule.
          // getSinisterKeyLabelByValue renvoyait la cle d'enum FRANCAISE,
          // d'ou un libelle en francais en session anglaise.
          label={translateSinisterStatus(t, data?.sinister_status)}
          value={data?.sinister_status}
        />
      );
    },
  },
  {
    header: t("common.actions", "Actions"),
    field: "claim_action",
    render: (data: Record<string, any>) => {
      return (
        <Tooltip title={t("claims.viewClaim", "View claim")}>
          <IconButton
            aria-label={t("claims.viewClaim", "View claim")}
            onClick={(e) => {
              e.stopPropagation();
              onViewClaim(data.id);
            }}
            size="small"
            sx={iconButtonSx}
          >
            <Icon name="eye" width={20} height={20} color={colors.primary} />
          </IconButton>
        </Tooltip>
      );
    },
  },
];
